'use strict'

import {
  CircularProgress,
  Tooltip
} from '@material-ui/core'
import BarChartIcon from '@material-ui/icons/BarChart'
import { addDays, getWeekTypeForDate } from './scheduleUtils'
import { formatMinutes } from './mapUtils'

/**
 * Desert Flora styled capacity details drawer.
 * .cap-drawer + .cap-drawer-scrim. Stats grid, week block with day chips, delta block.
 */
export default function CapacityDrawer ({
  capacityMatrix, capacityLoading,
  approvedRouteDays, approvedSet,
  focusRouteCapacityCell
}) {

  if (!capacityMatrix?.routes?.length) return null

  return (
    <div className="cap-drawer" style={{
      position: 'absolute', top: 0, right: 0, bottom: 0, width: 380, zIndex: 200,
      background: 'var(--df-surface, #fbf8f1)',
      borderLeft: '1px solid var(--df-line, #e0d8c2)',
      boxShadow: '-8px 0 28px rgba(28,36,25,0.12)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      animation: 'df-drawerIn 0.26s cubic-bezier(.16,1,.3,1)'
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px',
        borderBottom: '1px solid var(--df-line, #e0d8c2)',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BarChartIcon style={{ fontSize: 18, color: 'var(--df-primary, #1f5e3a)' }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--df-ink, #1c2419)', flex: 1 }}>
            Route Capacity (week of {capacityMatrix.weekOf || '?'})
          </span>
          {capacityLoading && <CircularProgress size={14} style={{ color: 'var(--df-primary, #1f5e3a)' }} />}
          {approvedRouteDays.length > 0 && (
            <span style={{ fontSize: 11, color: '#2f7a3e', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {approvedRouteDays.length} approved
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ overflowY: 'auto', flex: 1, padding: 16 }}>
        {/* Heatmap table */}
        <table style={{
          width: '100%', borderCollapse: 'collapse', fontSize: 12
        }}>
          <thead>
            <tr>
              <th style={{
                textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--df-ink-4, #97a094)',
                textTransform: 'uppercase', letterSpacing: '0.04em',
                padding: '6px 4px', borderBottom: '1px solid var(--df-line, #ede6d2)'
              }}>Route</th>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(d => (
                <th key={d} style={{
                  textAlign: 'center', fontSize: 10, fontWeight: 700,
                  color: 'var(--df-ink-4, #97a094)', textTransform: 'uppercase',
                  letterSpacing: '0.04em', padding: '6px 2px',
                  borderBottom: '1px solid var(--df-line, #ede6d2)'
                }}>{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {capacityMatrix.routes.map(route => {
              const days = route.days || {}
              const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
              return (
                <tr key={route.routeId}>
                  <td style={{
                    textAlign: 'left', fontWeight: 500, maxWidth: 100,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    paddingRight: 4, padding: '4px 4px', fontSize: 11.5,
                    color: 'var(--df-ink-2, #3d463a)'
                  }}>
                    <Tooltip title={`${route.routeName}${route.distanceMiles != null ? ` (${route.distanceMiles} mi)` : ''}`} arrow>
                      <span>{route.routeName?.replace(/\s*\(.*\)/, '') || route.routeId}</span>
                    </Tooltip>
                  </td>
                  {dayOrder.map((dayName, dayIdx) => {
                    const dm = days[dayName] || {}
                    const pct = dm.capacityPct || 0
                    const dotColor = pct > 85 ? '#a9341d' : pct > 60 ? '#b35d1a' : '#2f7a3e'
                    const cellWeekType = capacityMatrix.weekOf
                      ? getWeekTypeForDate(addDays(new Date(capacityMatrix.weekOf + 'T00:00:00'), dayIdx))
                      : ''
                    const isApproved = approvedSet.has(`${route.crewId}_${dayName}_${cellWeekType}`)
                    return (
                      <td
                        key={dayName}
                        style={{
                          textAlign: 'center', padding: '4px 2px',
                          cursor: 'pointer'
                        }}
                        onClick={() => focusRouteCapacityCell(route, dayIdx)}
                      >
                        <Tooltip
                          title={`${dm.stops || 0} stops · ${formatMinutes(dm.totalMin || 0)} / 8h (${Math.round(pct)}%)${isApproved ? ` · Approved (Wk ${cellWeekType})` : ''}`}
                          arrow
                        >
                          <span style={{
                            display: 'inline-block', width: 14, height: 14,
                            borderRadius: '50%', verticalAlign: 'middle',
                            backgroundColor: dotColor,
                            ...(isApproved && {
                              border: '2px solid #2f7a3e',
                              boxShadow: '0 0 0 1px #fff'
                            })
                          }} />
                        </Tooltip>
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
